import React, { useState, useEffect } from 'react';
import DayBadge from './DayBadge';
import ActivityTitle from '@/components/common/ActivityTitle';
import ItineraryInfoPopover, {
  TransferDisplayItem,
  type HotelFromItinerary,
  type TransferFromItinerary,
} from './ItineraryInfoPopover';
import { ItineraryDayProps } from '@/types/itineraryTypes';
import { BackendCurrency } from '@/utils/currencyHelpers';
import { HighlightsList } from '../highlights';
import { BenefitsList } from '../benefits';
import { OptimizedImage } from '@/components/common/OptimizedImage';
import { cacheImages } from '@/services/imageCacheService';
import type { Hotel } from '@/components/packages/Hotels';
import type { Vehicle } from '@/components/packages/Vehicles';
import type { NormalizedRoomOption } from '@/utils/roomOptionHelpers';

const ItineraryDay: React.FC<
  ItineraryDayProps & {
    isLast?: boolean;
    backendCurrency?: BackendCurrency;
    hotels?: Hotel[];
    vehicles?: Vehicle[];
    transfersForDay?: TransferDisplayItem[];
    /** When false, hide the (i) info icon. When true/undefined, show it when hotels/vehicles or itinerary hotel/room/transfer exist. */
    showItineraryInfoIcon?: boolean;
    /** When false, hide day benefits section. */
    showBenefits?: boolean;
    /** When true, render day hotel as highlight cards. */
    showHotelAsHighlights?: boolean;
    /** Hotel from itinerary item (when populated); used directly in info popover */
    hotelFromItinerary?: HotelFromItinerary;
    /** Room from itinerary item (when populated); used directly in info popover */
    roomFromItinerary?: {
      _id: string;
      name?: string;
      description?: string;
      imageUrl?: string[];
    };
    roomOptionsFromItinerary?: NormalizedRoomOption[];
    /** Transfer from itinerary item (when populated); used directly in info popover */
    transferFromItinerary?: TransferFromItinerary;
    /** True when itinerary is shown inside booking flow */
    isBookingContext?: boolean;
  }
> = ({
  day,
  title,
  description,
  imageUrls = [],
  imageUrl,
  benefits = [],
  highlights = [],
  isLast = false,
  backendCurrency,
  hotels = [],
  vehicles = [],
  transfersForDay = [],
  showItineraryInfoIcon = true,
  showBenefits = true,
  showHotelAsHighlights = false,
  roomId,
  hotelFromItinerary,
  roomFromItinerary,
  roomOptionsFromItinerary = [],
  transferFromItinerary,
  isBookingContext = false,
}) => {
  const [isDayExpanded, setIsDayExpanded] = useState(true);
  const images = React.useMemo(
    () =>
      Array.isArray(imageUrls) && imageUrls.length > 0
        ? imageUrls
        : Array.isArray(imageUrl)
          ? imageUrl
          : [],
    [imageUrls, imageUrl]
  );

  // Preload images in background
  useEffect(() => {
    if (images.length > 0) {
      cacheImages(images.slice(0, 5)).catch(() => {});
    }
  }, [images]);

  const getHotelCardImages = (
    hotel: {
      imageUrl?: string[];
      media?: Array<{ url?: string; format?: string }>;
    } | null
  ): string[] => {
    if (!hotel) return [];

    const imageUrls = Array.isArray(hotel.imageUrl)
      ? hotel.imageUrl.filter(Boolean)
      : [];
    if (imageUrls.length > 0) return imageUrls;

    return Array.isArray(hotel.media)
      ? hotel.media
          .filter(item => item?.format === 'image' && Boolean(item?.url))
          .map(item => item.url as string)
      : [];
  };

  const hotelHighlights = React.useMemo(() => {
    if (!showHotelAsHighlights) return [];

    const sourceHotels =
      Array.isArray(hotels) && hotels.length > 0
        ? hotels
        : hotelFromItinerary && hotelFromItinerary.name
          ? [hotelFromItinerary]
          : [];

    return sourceHotels
      .filter(h => Boolean(h?.name))
      .map((h, idx) => ({
        _id: `${day}-hotel-${idx}-${h.name}`,
        title: h.name,
        description: (h as { description?: string }).description || '',
        imageUrl: getHotelCardImages(
          h as {
            imageUrl?: string[];
            media?: Array<{ url?: string; format?: string }>;
          }
        ),
        locations:
          (h as { location?: string; address?: string }).location ||
          (h as { location?: string; address?: string }).address
            ? {
                name: [
                  (h as { location?: string; address?: string }).location,
                  (h as { location?: string; address?: string }).address,
                ]
                  .filter(Boolean)
                  .join(', '),
              }
            : undefined,
      }));
  }, [showHotelAsHighlights, hotelFromItinerary, hotels, day]);

  const roomHighlights = React.useMemo(() => {
    if (!showHotelAsHighlights) return [];

    const sourceRooms =
      roomOptionsFromItinerary.length > 0
        ? roomOptionsFromItinerary
        : roomFromItinerary?.name
          ? [
              {
                id: roomFromItinerary._id || roomFromItinerary.name,
                name: roomFromItinerary.name,
                description: roomFromItinerary.description || '',
                imageUrl: Array.isArray(roomFromItinerary.imageUrl)
                  ? roomFromItinerary.imageUrl
                  : [],
              },
            ]
          : [];

    return sourceRooms
      .filter(room => Boolean(room?.name))
      .map((room, idx) => ({
        _id: `${day}-room-${room.id || idx}-${room.name}`,
        title: room.name,
        description: room.description || '',
        imageUrl: Array.isArray(room.imageUrl) ? room.imageUrl : [],
      }));
  }, [showHotelAsHighlights, roomOptionsFromItinerary, roomFromItinerary, day]);

  const shouldShowRoomHighlights =
    roomHighlights.length > 0 || isBookingContext;

  return (
    <div className='relative pl-5 mb-0 pb-8 border-l-2 border-dashed border-[#D9D9D9]'>
      <DayBadge day={day} />
      <div className='ml-2'>
        <div
          className='flex flex-wrap items-start justify-between gap-2 cursor-pointer'
          onClick={() => setIsDayExpanded(!isDayExpanded)}
        >
          <div className='flex items-center gap-2 min-w-0 flex-1'>
            <ActivityTitle
              title={title}
              className='!text-[18px] md:!mb-2 !mt-0'
            />
            {showItineraryInfoIcon &&
            ((Array.isArray(hotels) && hotels.length > 0) ||
              (Array.isArray(vehicles) && vehicles.length > 0) ||
              Boolean(hotelFromItinerary) ||
              Boolean(roomFromItinerary ?? roomId) ||
              Boolean(transferFromItinerary)) ? (
              <div onClick={e => e.stopPropagation()}>
                <ItineraryInfoPopover
                  hotels={hotels}
                  vehicles={vehicles}
                  transfersForDay={transfersForDay}
                  roomId={
                    typeof roomId === 'object'
                      ? {
                          _id: roomId._id || roomId.id || '',
                          name: roomId.name,
                          description: roomId.description,
                        }
                      : undefined
                  }
                  hotelFromItinerary={hotelFromItinerary}
                  roomFromItinerary={roomFromItinerary}
                  roomOptionsFromItinerary={roomOptionsFromItinerary}
                  transferFromItinerary={transferFromItinerary}
                />
              </div>
            ) : null}
          </div>
          <div className='flex items-center gap-2'>
            <span
              className={`ml-1 shrink-0 transition-transform duration-300 ${isDayExpanded ? 'rotate-180' : ''}`}
            >
              <svg
                width='12'
                height='7'
                viewBox='0 0 12 7'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M5.9998 6.97481C5.86647 6.97481 5.73747 6.9498 5.6128 6.89981C5.48747 6.8498 5.38314 6.78314 5.2998 6.69981L0.699804 2.09981C0.516471 1.91647 0.424805 1.68314 0.424805 1.3998C0.424805 1.11647 0.516471 0.883138 0.699804 0.699804C0.883138 0.516471 1.11647 0.424805 1.3998 0.424805C1.68314 0.424805 1.91647 0.516471 2.0998 0.699804L5.9998 4.59981L9.8998 0.699804C10.0831 0.516471 10.3165 0.424805 10.5998 0.424805C10.8831 0.424805 11.1165 0.516471 11.2998 0.699804C11.4831 0.883138 11.5748 1.11647 11.5748 1.3998C11.5748 1.68314 11.4831 1.91647 11.2998 2.09981L6.6998 6.69981C6.5998 6.79981 6.49147 6.87047 6.3748 6.91181C6.25814 6.9538 6.13314 6.97481 5.9998 6.97481Z'
                  fill='#7A7A7A'
                />
              </svg>
            </span>
          </div>
        </div>
        <p className='text-black text-sm'>{description}</p>

        {/* Images gallery for the day */}
        {images.length > 0 && (
          <div className='mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3'>
            {images.map((url, idx) => (
              <div
                key={`${idx}-${url}`}
                className='relative rounded-md overflow-hidden !w-[180px] !h-[90px]'
                style={{
                  width: '180px',
                  height: '90px',
                }}
              >
                <OptimizedImage
                  src={url || '/assets/images/No-Image.jpg'}
                  alt={`Itinerary image ${idx + 1}`}
                  fill
                  style={{ objectFit: 'cover' }}
                  className='rounded-md'
                  sizes='180px'
                  loading={idx < 3 ? 'eager' : 'lazy'}
                  priority={idx < 3}
                />
              </div>
            ))}
          </div>
        )}
        {isDayExpanded && (
          <div>
            {showBenefits && <BenefitsList benefits={benefits} />}
            <div className='flex flex-wrap items-start gap-8'>
              <div>
                <HighlightsList
                  highlights={hotelHighlights}
                  backendCurrency={backendCurrency}
                  title='Hotel:'
                />
              </div>
              {shouldShowRoomHighlights && (
                <div>
                  <HighlightsList
                    highlights={roomHighlights}
                    backendCurrency={backendCurrency}
                    title='Room:'
                  />
                </div>
              )}
            </div>
            <HighlightsList
              highlights={highlights}
              backendCurrency={backendCurrency}
            />
          </div>
        )}
      </div>
      {isLast && (
        <DayBadge day='End' label='end' position='bottom' showLabel={false} />
      )}
    </div>
  );
};

export default ItineraryDay;
